// ---------------------------------------------------------------------------
// Logo Cloud Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface Logo {
  imageUrl: string;
  alt: string;
  linkUrl?: string;
}

interface LogoCloudProps {
  heading?: string;
  logos: Logo[];
  animate?: boolean;
  grayscale?: boolean;
  size?: "sm" | "md" | "lg";
}

const SIZE_MAP: Record<string, string> = {
  sm: "32px",
  md: "48px",
  lg: "64px",
};

function renderLogoItem(logo: Logo, height: string, grayscale: boolean): string {
  const filterStyle = grayscale ? " filter: grayscale(100%);" : "";
  const numericHeight = Number.parseInt(height, 10);
  const heightAttr = Number.isFinite(numericHeight) ? ` height="${attr(String(numericHeight))}"` : "";
  const img = `<img src="${url(logo.imageUrl)}" alt="${attr(logo.alt)}" class="ease-logo-cloud-img" loading="lazy"${heightAttr} style="height: ${attr(height)}; width: auto; object-fit: contain;${filterStyle}">`;

  if (logo.linkUrl) {
    return `<a href="${url(logo.linkUrl)}" class="ease-logo-cloud-link" target="_blank" rel="noopener noreferrer">${img}</a>`;
  }

  return img;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as LogoCloudProps;

  const logos = Array.isArray(p.logos) ? p.logos : [];
  const isAnimated = p.animate === true;
  const isGrayscale = p.grayscale === true;
  const size = p.size ?? "md";
  const height = SIZE_MAP[size] ?? "48px";
  const ariaLabel = p.heading ?? "Partners";

  const headingHtml = p.heading
    ? `\n  <p class="ease-logo-cloud-heading">${text(p.heading)}</p>`
    : "";

  const logoItems = logos
    .map((logo) => renderLogoItem(logo, height, isGrayscale))
    .join("\n      ");

  if (isAnimated) {
    // Duplicate logos for seamless infinite scroll
    const duplicateItems = logos
      .map((logo) => renderLogoItem(logo, height, isGrayscale))
      .join("\n      ");

    return `<section class="ease-section ease-logo-cloud" aria-label="${attr(ariaLabel)}">${headingHtml}
  <div class="ease-logo-cloud-container" style="overflow: hidden;">
    <div class="ease-logo-cloud-track" style="gap: var(--ease-spacing-lg, 2rem); align-items: center;">
      ${logoItems}
      ${duplicateItems}
    </div>
  </div>
</section>`;
  }

  return `<section class="ease-section ease-logo-cloud" aria-label="${attr(ariaLabel)}">${headingHtml}
  <div class="ease-logo-cloud-container" style="display: flex; flex-wrap: wrap; gap: var(--ease-spacing-lg, 2rem); align-items: center; justify-content: center;">
    ${logoItems}
  </div>
</section>`;
}

registerRenderer("logo-cloud", { render });
